import React, { useState, useMemo, useEffect } from 'react';
import { Search, TestTube2, Clock, Droplet } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';

export const LabTests = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [firebaseTests, setFirebaseTests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Static fallback tests
  const staticLabTests = [
    { id: 1, testName: 'GLUCOSE, FBS', price: 85.0, category: 'Diabetic', sample: 'Blood', popular: true },
    { id: 2, testName: 'UREA', price: 130.0, category: 'Kidney', sample: 'Blood' },
    { id: 3, testName: 'CREATININE', price: 190.0, category: 'Kidney', sample: 'Blood' },
    { id: 4, testName: 'ALT/SGPT', price: 210.0, category: 'Liver', sample: 'Blood' },
    { id: 5, testName: 'AST/SGOT', price: 210.0, category: 'Liver', sample: 'Blood' },
    { id: 6, testName: 'SODIUM-SERUM', price: 190.0, category: 'Blood', sample: 'Blood' },
    { id: 7, testName: 'POTASSIUM - SERUM', price: 190.0, category: 'Blood', sample: 'Blood' },
    { id: 8, testName: 'T3 T4 TSH THYROID PROFILE TOTAL', price: 570.0, category: 'Thyroid', sample: 'Blood', popular: true },
    { id: 9, testName: 'LFT', price: 850.0, category: 'Liver', sample: 'Blood', popular: true },
    { id: 10, testName: 'LIPID PROFILE', price: 800.0, category: 'Cardiac', sample: 'Blood', popular: true },
    { id: 11, testName: 'URIC ACID', price: 150.0, category: 'Blood', sample: 'Blood' },
    { id: 12, testName: 'COMPLETE URINE EXAMINATION', price: 100.0, category: 'Blood', sample: 'Urine', popular: true },
    { id: 13, testName: 'CULTURE AND SENSITIVITY URINE', price: 565.0, category: 'Blood', sample: 'Urine' },
    { id: 14, testName: 'STOOL R/E', price: 130.0, category: 'Blood', sample: 'Stool' },
    { id: 15, testName: 'STOOL C/S', price: 750.0, category: 'Blood', sample: 'Stool' },
    { id: 16, testName: 'STOOL FOR OBT', price: 150.0, category: 'Blood', sample: 'Stool' },
    { id: 17, testName: 'Hb%', price: 110.0, category: 'Blood', sample: 'Blood', popular: true },
    { id: 18, testName: 'BLOOD GROUP ABO AND RH FACTOR', price: 130.0, category: 'Blood', sample: 'Blood', popular: true },
    { id: 19, testName: 'HIV I AND II ANTIBODIES (ELISA/ECLIA)', price: 550.0, category: 'Blood', sample: 'Blood' },
    { id: 20, testName: 'HBSAG/AUSTRALIA ANTIGEN (ELISA/CMIA)', price: 550.0, category: 'Blood', sample: 'Blood' },
    { id: 21, testName: 'MP', price: 110.0, category: 'Blood', sample: 'Blood' },
    { id: 22, testName: 'BILIRUBIN TOTAL', price: 150.0, category: 'Liver', sample: 'Blood' },
    { id: 23, testName: 'CALCIUM', price: 170.0, category: 'Blood', sample: 'Blood' },
    { id: 24, testName: 'FSH', price: 550.0, category: 'Blood', sample: 'Blood' },
    { id: 25, testName: 'LIPASE', price: 580.0, category: 'Blood', sample: 'Blood' },
    { id: 26, testName: 'FT3, FT4, TSH', price: 1000.0, category: 'Thyroid', sample: 'Blood' },
    { id: 27, testName: 'PSA TOTAL', price: 730.0, category: 'Blood', sample: 'Blood' },
    { id: 28, testName: 'CRP, C-REACTIVE PROTEIN', price: 420.0, category: 'Blood', sample: 'Blood' },
    { id: 29, testName: 'PLATELET COUNT', price: 120.0, category: 'Blood', sample: 'Blood' },
    { id: 30, testName: 'KFT', price: 850.0, category: 'Kidney', sample: 'Blood' },
    { id: 31, testName: 'ANTI CCP', price: 2000.0, category: 'Blood', sample: 'Blood' },
    { id: 32, testName: 'HCV', price: 440.0, category: 'Blood', sample: 'Blood' },
    { id: 33, testName: 'ANTI HCV', price: 1600.0, category: 'Blood', sample: 'Blood' },
    { id: 34, testName: 'URINE ACR', price: 600.0, category: 'Blood', sample: 'Urine' },
    { id: 35, testName: 'HS CRP', price: 760.0, category: 'Cardiac', sample: 'Blood' },
    { id: 36, testName: 'DENGUE NS1 ANTEGEN', price: 1500.0, category: 'Blood', sample: 'Blood' },
    { id: 37, testName: 'VITAMIN B12', price: 1100.0, category: 'Blood', sample: 'Blood' },
    { id: 38, testName: 'PROTEIN ELECTROPHORESIS SERUM', price: 700.0, category: 'Blood', sample: 'Blood' },
    { id: 39, testName: 'FERRITIN', price: 650.0, category: 'Blood', sample: 'Blood' },
    { id: 40, testName: 'THYROID STIMULATING HORMONE (TSH)', price: 220.0, category: 'Thyroid', sample: 'Blood', popular: true },
    { id: 41, testName: 'HBA1C, GLYCATED HEMOGLOBIN', price: 450.0, category: 'Diabetic', sample: 'Blood', popular: true },
    { id: 42, testName: 'HEMOGRAM', price: 400.0, category: 'Blood', sample: 'Blood', popular: true },
    { id: 43, testName: 'SWASTH SUPER 1', price: 1050.0, category: 'Package', sample: 'Multiple' },
    { id: 44, testName: 'IMMUNOGLOBULIN, IGE SERUM', price: 830.0, category: 'Blood', sample: 'Blood' },
    { id: 45, testName: 'TSH', price: 220.0, category: 'Thyroid', sample: 'Blood' },
    { id: 46, testName: 'RHEUMATOID FACTOR (RA), SERUM', price: 600.0, category: 'Blood', sample: 'Blood' },
    { id: 47, testName: 'BETA HCG (MATERNAL)', price: 650.0, category: 'Blood', sample: 'Blood' },
    { id: 48, testName: 'SPUTAM GRAM STAIN C/S', price: 950.0, category: 'Blood', sample: 'Sputum' },
    { id: 49, testName: 'URINE ROUTINE EXAMINATION', price: 100.0, category: 'Blood', sample: 'Urine' },
    { id: 50, testName: 'SWASTH SUPER-2', price: 1350.0, category: 'Package', sample: 'Multiple' },
    { id: 51, testName: 'ESR', price: 130.0, category: 'Blood', sample: 'Blood' },
    { id: 52, testName: 'SWASTH SUPER-4', price: 2350.0, category: 'Package', sample: 'Multiple' },
    { id: 53, testName: 'GLUCOSE, POST PRANDIAL, 2 HOURS', price: 85.0, category: 'Diabetic', sample: 'Blood' },
    { id: 54, testName: 'STOOL FOR FECALPROTECTIN', price: 3000.0, category: 'Blood', sample: 'Stool' },
    { id: 55, testName: 'VITAMIN D 3 25 (OH)', price: 1400.0, category: 'Blood', sample: 'Blood' },
    { id: 56, testName: 'COGULATION PROFILE', price: 960.0, category: 'Blood', sample: 'Blood' },
    { id: 57, testName: 'FT4, TSH', price: 700.0, category: 'Thyroid', sample: 'Blood' },
    { id: 58, testName: 'MPDA', price: 700.0, category: 'Blood', sample: 'Blood' },
    { id: 59, testName: 'WIDAL', price: 180.0, category: 'Blood', sample: 'Blood' },
    { id: 60, testName: 'SWAST SUPER 3', price: 2050.0, category: 'Package', sample: 'Multiple' },
    { id: 61, testName: 'HLA-B27, FLOW CYTOMETRY', price: 2400.0, category: 'Blood', sample: 'Blood' },
    { id: 62, testName: 'PROTEIN, TOTAL', price: 170.0, category: 'Blood', sample: 'Blood' },
  ];

  // Fetch tests from Firebase in real-time
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'lab_tests'),
      (snapshot) => {
        const testsData = [];
        snapshot.forEach((doc) => {
          testsData.push({ id: doc.id, ...doc.data() });
        });
        testsData.sort((a, b) => (a.testName || '').localeCompare(b.testName || ''));
        setFirebaseTests(testsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching tests:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Merge Firebase tests with static tests (Firebase takes priority, no duplicates)
  const labTests = useMemo(() => {
    // Create a map of Firebase tests by testName for quick lookup
    const firebaseTestMap = new Map();
    firebaseTests.forEach(test => {
      if (test.testName) {
        firebaseTestMap.set(test.testName.toLowerCase().trim(), test);
      }
    });

    // Start with all Firebase tests
    const merged = [...firebaseTests];

    // Add static tests that don't exist in Firebase
    staticLabTests.forEach(staticTest => {
      const key = staticTest.testName.toLowerCase().trim();
      if (!firebaseTestMap.has(key)) {
        merged.push(staticTest);
      }
    });

    // Sort by test name
    merged.sort((a, b) => (a.testName || '').localeCompare(b.testName || ''));
    return merged;
  }, [firebaseTests]);

  const categories = ['All', 'Popular', 'Blood', 'Diabetic', 'Thyroid', 'Cardiac', 'Liver', 'Kidney', 'Package'];

  const filteredTests = useMemo(() => {
    return labTests.filter((test) => {
      const matchesSearch = test.testName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === 'All' ||
        (selectedCategory === 'Popular' && test.popular) ||
        test.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory, labTests]);

  const handleBookHomeCollection = (testName) => {
    const message = encodeURIComponent(
      `Hi Ushakiran Medicos, I want to book a home collection for ${testName}. Please help me.`
    );
    window.open(`https://wa.me/919007667528?text=${message}`, '_blank');
  };

  return (
    <section
      id="tests"
      data-testid="lab-tests-section"
      className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50"
    >
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-4">
            <span className="text-gradient-primary">Lab Tests</span> & Diagnostic Services
          </h2>
          <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Search from our comprehensive catalog of lab tests with home sample collection facility
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              data-testid="lab-tests-search-input"
              type="text"
              placeholder="Search for tests (e.g., CBC, Thyroid, Glucose)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 text-base border-slate-300 rounded-xl focus:ring-2 focus:ring-[#0284C7]"
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-3 justify-center mb-8 sm:mb-12">
          {categories.map((category) => (
            <Badge
              key={category}
              data-testid={`lab-test-category-chip-${category.toLowerCase()}`}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-[#0284C7] to-[#059669] text-white hover:shadow-lg'
                  : 'bg-white text-slate-700 border border-slate-300 hover:border-[#0284C7]'
              }`}
            >
              {category}
            </Badge>
          ))}
        </div>

        {/* Tests Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTests.map((test) => (
            <div
              key={test.id}
              data-testid={`lab-test-card-${test.id}`}
              className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_32px_rgba(2,132,199,0.12)] transition-all duration-300 hover:border-sky-300"
            >
              <div className="flex flex-col h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800 text-base mb-2 leading-tight">
                      {test.testName}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                      <Droplet className="w-3 h-3" />
                      <span>{test.sample}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>Report in 24-48 hrs</span>
                    </div>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 font-bold text-base">
                    ₹{test.price.toFixed(2)}
                  </Badge>
                </div>

                <Button
                  data-testid={`book-home-collection-btn-${test.id}`}
                  onClick={() => handleBookHomeCollection(test.testName)}
                  className="w-full mt-auto bg-gradient-to-r from-[#0284C7] to-[#059669] text-white hover:shadow-lg transition-all duration-300"
                >
                  <TestTube2 className="w-4 h-4 mr-2" />
                  Book Home Collection
                </Button>
              </div>
            </div>
          ))}
        </div>

        {filteredTests.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500 text-lg">No tests found matching your search.</p>
          </div>
        )}
      </div>
    </section>
  );
};